import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, map } from 'lodash';

import AgGridTableContainer from '../../table/ag-grid-table-container';
import ColumnFilterHeader from '../../table/filters/column-filter-header';
import LoadingSpinner from '../../common/components/loading-spinner';
import { TABLE_TYPES } from '../../table/constants/table-options';
import {
    DMARC_NONE,
    DMARC_REJECT,
    DMARC_STATUS_NO_RECORD,
    DMARC_STATUS_INVALID,
    DMARC_QUARANTINE
} from '../../common/constants/dmarc-policy-values';
import { translate } from '../../utils/translation-utils';

import { activeClass } from '../../table/table-utils';
import { calculateRate } from '../../utils/common-utils';
import {
    dmarcPolicyIconRenderer,
    dmarcPolicyCalculator,
} from '../../table/table-utils';

import {
    exportToCSVFailure,
    exportToCSVSuccess,
    resetWidgetTableColumn,
    toggleWidgetTableColumn
} from '../../dashboard/actions/dashboard-action-creators';
import { fetchAggResultsByCanvasId } from '../../common/actions/aggregate-results-action-creators';
import { initializeMyDomainsWidget } from './my-domains-action-creators';
import { openDomainDetailsPane } from '../../common/actions/right-pane-action-creators';
import {
    isActiveRightPaneSelector,
} from '../../common/selectors/right-pane-selectors';
import { myDomainsTableDataSelector, myDomainsDataIsLoadingSelector } from './my-domains-selectors';
import { isExportingGridToCsvSelector } from '../../dashboard/selectors/widget-selectors';

export class MyDomainsTableContainer extends Component{
    componentDidMount(){
        this.fetchTheData();
    }

    componentDidUpdate( prevProps ){
        if( this.props.canvasDataParams !== prevProps.canvasDataParams ){
            this.fetchTheData();
        }
    }

    buildCustomGridOptions = () => {
        return {
            columnTypes: {
                groupableColumn: {
                    cellRenderer: 'agGroupCellRenderer',
                    cellStyle: {
                        paddingLeft: 0
                    },
                    minWidth: 160,
                    showRowGroup: true,
                },
                hiddenGroupColumn: {
                    hide: true,
                    rowGroup: true,
                },
                policyIconColumn: {
                    cellRendererFramework: dmarcPolicyIconRenderer,
                    comparator: ( policyA, policyB, nodeA, nodeB ) => {
                        /* for some reason, filtering triggers the comparator for policyIconColumn &
                         * both nodes are undefined on the filter
                         */
                        if( nodeA && nodeB ){
                            const dataA = get( nodeA, 'group', false ) ? nodeA.aggData : nodeA.data;
                            const isInheritedA = get( dataA, 'dmarcPolicyInherited', 'explicit' );

                            const dataB = get( nodeB, 'group', false ) ? nodeB.aggData : nodeB.data;
                            const isInheritedB = get( dataB, 'dmarcPolicyInherited', 'explicit' );

                            const policyIntA = dmarcPolicyCalculator( policyA, isInheritedA );
                            const policyIntB = dmarcPolicyCalculator( policyB, isInheritedB );

                            return policyIntA - policyIntB;
                        }
                    },
                    filterParams: {
                        cellRenderer: ( params ) => translate( params.value ),
                        // Keep order of the value for the filter
                        // `p=none`, `p=quarantine`, `p=reject`, `Invalid record`, `No record`
                        values: [
                            DMARC_NONE,
                            DMARC_QUARANTINE,
                            DMARC_REJECT,
                            DMARC_STATUS_INVALID,
                            DMARC_STATUS_NO_RECORD
                        ],
                    },
                    valueGetter: ( params ) => {
                        const status = get( params, 'data.dmarcStatus', null );

                        if( status === 'invalid' || status === 'no_record' ){
                            return status;
                        }
                        else{
                            return get( params, `data.dmarcPolicy`, null );
                        }
                    },
                },
            },
            groupRowAggNodes: this.calculateGroupedRowTotals,
            postSort: ( rowNodes ) => {
                for( let i = 0; i < rowNodes.length; i++ ){
                    const domainName = get( rowNodes[i], 'data.domainName', false );
                    const orgDomain = get( rowNodes[i], 'data.orgDomain', false );
                    if( domainName && ( domainName === orgDomain ) ){
                        // display parent org domain before other subdomains for grouped row view
                        let currentNode = rowNodes.splice( i, 1 )[0];
                        rowNodes.splice( 0, 0, currentNode );
                    }
                }
            },
            rowClassRules: {
                'capitalize-parent-group': ( params ) => params.node.group,
                'row-child': ( params ) =>  params.node.uiLevel === 1,
            },
        };
    }

    fetchTheData = () => {
        const { config, initializeMyDomainsWidget } = this.props;
        initializeMyDomainsWidget( config.id );
    }

    handleColumnToggle = ( columnId ) => {
        this.props.toggleWidgetTableColumn( this.props.config.id, columnId );
    }

    handleColumnReset = () => {
        this.props.resetWidgetTableColumn( this.props.config.id );
    }

    handleRowClick = ( row ) => {
        this.props.openDomainDetailsPane( row.domainName );
    }

    calculateWeightedRate = ( field, rateToCountMap, formattedData, rowData ) => {
        if( field in rateToCountMap ){
            // set value of current field rate to count
            rateToCountMap[field].value += rowData[rateToCountMap[field].name];

            // new row value based on updated rate calculation
            return calculateRate( formattedData.authorizedMailVolume, rateToCountMap[field].value );
        }

        return formattedData[field] += rowData[field];
    }

    calculateGroupedRowTotals = ( nodes ) => {
        const { config: { agGrid } } = this.props;
        const rateToCountMap = {};
        const formattedData = {};
        const headerFieldNames = map( agGrid, 'field' );

        // not a column but we need it for sorting
        headerFieldNames.push( 'dmarcPolicyInherited' );

        /* Initializes rateToCountMap & formattedData to look like the following:
         *  formattedData = {
         *      actionsCount: 0,
         *      authorizedMailVolume: 0,
         *      dkimAlignRate: 0,
         *      ...
         *  }
         *  rateToCountMap = {
         *      dmarcPassRate: { name: 'dmarcPassCount', value: 0 },
         *      dkimPassRate: {name: 'dkimPassCount', value: 0},
         *      ...
         *  }
         */
        agGrid.forEach( header => {
            const fieldName = header.field;
            if( header.type.includes( 'numberColumn' ) || header.type.includes( 'rateColumn' ) ){
                formattedData[fieldName] = 0;

                if( get( header, 'fieldParams.rateCountField', false ) ){
                    rateToCountMap[fieldName] = { name: header.fieldParams.rateCountField, value: 0 };
                }
            }
        } );

        nodes.forEach( ( node ) => {
            const nodeData = node.data;
            // ag-grid creates a pseudo group of all un-grouped nodes sans data, so this filters those out
            if( nodeData ){
                headerFieldNames.forEach( ( field ) => {
                    // perform calculation on any grouped numeric field
                    let formattedValue = nodeData[field];
                    if( nodes.length > 1 ){
                        if( typeof nodeData[field] === 'number' ){
                            // finds correct count based on mapped data inside of rateCountTotals
                            formattedValue = this.calculateWeightedRate(
                                field, rateToCountMap, formattedData, nodeData
                            );
                        }
                        else{
                            // for parent of rowGroup, set non-numeric fields to null
                            formattedValue = null;
                        }
                    }
                    formattedData[field] = formattedValue;
                } );
            }
        } );

        return formattedData;
    }

    render(){
        const {
            config, exportToCSVSuccess, exportToCSVFailure, isActiveTable, isExportingGridToCSV, isLoading,
            myDomainsTableData
        } = this.props;

        const dataAutomationId = 'my-domains-table';

        if( isLoading ){
            return <LoadingSpinner />;
        }

        return (
            <>
                <ColumnFilterHeader
                    columnDefs={config.agGrid}
                    dataAutomationId={dataAutomationId}
                    description={ `${myDomainsTableData.length} domains` }
                    onColumnToggle={this.handleColumnToggle}
                    onColumnReset={this.handleColumnReset}
                    persistColumnFilters={[ 'orgDomain', 'domainName' ]}
                />
                <AgGridTableContainer
                    columnDefs={config.agGrid}
                    customGridOptions={this.buildCustomGridOptions()}
                    dataAutomationId={dataAutomationId}
                    rowClickHandler={this.handleRowClick}
                    rowData={myDomainsTableData}
                    exportToCSVSuccess={exportToCSVSuccess}
                    exportToCSVFailure={exportToCSVFailure}
                    exportCSVFileName="My Domains"
                    isExportingGridToCSV={isExportingGridToCSV}
                    tableType={`${TABLE_TYPES.SCROLLING} ${TABLE_TYPES.WIDGET} ${TABLE_TYPES.GROUPED} ${activeClass( isActiveTable )}`}
                    widgetId={config.id}
                />
            </>
        );
    }
};

/* istanbul ignore next */
const mapStateToProps = ( state, props ) => ( {
    isActiveTable: isActiveRightPaneSelector( state, 'domainDetails' ),
    isExportingGridToCSV: isExportingGridToCsvSelector( state, props ),
    isLoading: myDomainsDataIsLoadingSelector( state ),
    myDomainsTableData: myDomainsTableDataSelector( state, props ),
} );

const mapDispatchToProps = {
    exportToCSVFailure,
    exportToCSVSuccess,
    fetchAggResultsByCanvasId,
    initializeMyDomainsWidget,
    openDomainDetailsPane,
    resetWidgetTableColumn,
    toggleWidgetTableColumn,
};

MyDomainsTableContainer.propTypes = {
    canvasDataParams: PropTypes.object.isRequired,
    config: PropTypes.shape( {
        id: PropTypes.number.isRequired,
    } ).isRequired,
    exportToCSVFailure: PropTypes.func.isRequired,
    exportToCSVSuccess: PropTypes.func.isRequired,
    initializeMyDomainsWidget: PropTypes.func.isRequired,
    isActiveTable: PropTypes.bool,
    isExportingGridToCSV: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    myDomainsTableData: PropTypes.array,
    openDomainDetailsPane: PropTypes.func,
    resetWidgetTableColumn: PropTypes.func.isRequired,
    toggleWidgetTableColumn: PropTypes.func.isRequired,
};

export default connect( mapStateToProps, mapDispatchToProps )( MyDomainsTableContainer );
